import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import App, { ErrorBoundary } from './App.tsx';
import { safeStringify } from './lib/utils';
import { localDb } from './services/localDbService';
import './index.css';

// Sanitize console outputs to prevent AI Studio preview platform from crashing
// when intercepting console logs that contain circular structures (like Firestore errors)
['error', 'warn'].forEach((method) => {
  const original = (console as any)[method];
  (console as any)[method] = (...args: any[]) => {
    const isQuotaError = args.some(arg => {
      if (arg && (arg.name === 'QuotaExceededError' || arg.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
        return false;
      }
      const str = typeof arg === 'string' ? arg : (arg instanceof Error ? `${arg.name} ${arg.message}` : String(arg));
      const lower = str.toLowerCase();
      if (lower.includes('localstorage') || lower.includes('storage proxy') || lower.includes('quotaexceedederror')) {
        return false;
      }
      return lower.includes('resource-exhausted') || 
             lower.includes('resource_exhausted') || 
             (lower.includes('firestore') && (lower.includes('quota exceeded') || lower.includes('quota limit exceeded')));
    });

    if (isQuotaError) {
      if (typeof window !== 'undefined') {
        (window as any).__firestoreQuotaExceeded = true;
        window.dispatchEvent(new CustomEvent('firestore-quota-exceeded'));
      }
      // Log as a warm handled message instead of printing raw red-error traces to prevent system alarms
      original.apply(console, [`[Firestore Quota Gracefully Handled] (App automatically shifted to Local Storage offline fallback):`, ...args.map(arg => {
        if (arg instanceof Error) {
          return `${arg.name}: ${arg.message}`;
        }
        return arg;
      })]);
      return;
    }

    const safeArgs = args.map(arg => {
      if (arg instanceof Error) {
        return `[${arg.name}: ${arg.message}] \n${arg.stack}`;
      }
      if (arg && typeof arg === 'object') {
        try {
          JSON.stringify(arg);
          return arg;
        } catch (e) {
          return safeStringify(arg);
        }
      }
      return arg;
    });
    original.apply(console, safeArgs);
  };
});

let appMounted = false;
function mountApp() {
  if (appMounted) return;
  appMounted = true;
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </ErrorBoundary>
    </StrictMode>,
  );
}

// Fallback timer to guarantee React app mounts within 3.5s
const mountFallbackTimer = setTimeout(() => {
  console.warn('[App Boot] Boot fallback timer triggered. Mounting React app.');
  mountApp();
}, 3500);

// Initialize localDb (IndexedDB) first, then mount the React root
localDb.init()
  .then(() => {
    clearTimeout(mountFallbackTimer);
    console.log('[App Boot] LocalDB successfully initialized.');
    
    // Install transparent global localStorage proxy for heavy/growing datasets
    if (typeof window !== 'undefined' && window.localStorage) {
      const originalSetItem = window.localStorage.setItem.bind(window.localStorage);
      const originalGetItem = window.localStorage.getItem.bind(window.localStorage);
      const originalRemoveItem = window.localStorage.removeItem.bind(window.localStorage);
      const originalClear = window.localStorage.clear.bind(window.localStorage);

      // Save original methods to avoid infinite loops when services bypass proxy
      (window as any).__originalGetItem = originalGetItem;
      (window as any).__originalSetItem = originalSetItem;
      (window as any).__originalRemoveItem = originalRemoveItem;
      (window as any).__originalClear = originalClear;

      const heavyKeys = [
        'meal_history',
        'chat_history',
        'scanned_products',
        'water_logs',
        'grocery_items',
        'favorites',
        'user_goals',
        'ai_meal_suggestions_cache',
        'ai_excluded_foods'
      ];

      const isHeavyKey = (key: string) => {
        return heavyKeys.includes(key) || 
               key.startsWith('pending_sync_') || 
               key.startsWith('pending_water_sync_');
      };

      window.localStorage.setItem = (key: string, value: string) => {
        if (isHeavyKey(key)) {
          localDb.setItem(key, value);
          return;
        }
        try {
          originalSetItem(key, value);
        } catch (e: any) {
          if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED' || String(e).includes('quota')) {
            console.info(`[Storage Proxy] Browser storage limit reached for key "${key}". Redirected to IndexedDB.`);
            localDb.setItem(key, value);
          } else {
            throw e;
          }
        }
      };

      window.localStorage.getItem = (key: string) => {
        let val;
        if (isHeavyKey(key)) {
          val = localDb.getItemSync(key);
        } else {
          val = originalGetItem(key) ?? localDb.getItemSync(key);
        }
        if (val === "undefined" || val === "null") return null;
        return val;
      };

      window.localStorage.removeItem = (key: string) => {
        if (isHeavyKey(key)) {
          localDb.removeItem(key);
          return;
        }
        originalRemoveItem(key);
      };

      console.log('[App Boot] Installed global localStorage proxy for heavy datasets.');
    }

    mountApp();
  })
  .catch((err) => {
    clearTimeout(mountFallbackTimer);
    console.error('[App Boot] LocalDB failed to initialize. Falling back to default localStorage behavior.', err);
    mountApp();
  });

// Register Service Worker for PWA & Notification support (iOS requires this)
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  const hadController = !!navigator.serviceWorker.controller;
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Only reload on updates if the page was ALREADY controlled by an older worker
    if (hadController && !refreshing) {
      refreshing = true;
      console.log('[PWA] New version activated. Reloading...');
      window.location.reload();
    }
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => {
        console.log('PWA Service Worker registered successfully:', reg.scope);
        // Prompt immediate update check on mobile browsers / PWA
        reg.update().catch(() => {});
      })
      .catch((err) => {
        console.warn('PWA Service Worker registration failed:', err);
      });
  });
}
